/**
 * Déroulement d'une séance de révision (spécification §5) : phase 1, puis phases N avec les
 * cartes encore en apprentissage qui reviennent dans la fenêtre de débordement. Aucun
 * « coupe-file » : une carte dont l'heure arrive ne passe jamais devant les autres.
 *
 * Une séance est une valeur immuable : chaque action renvoie une nouvelle séance.
 * L'enregistrement des révisions (base de données) est fait par l'appelant.
 */
import {
  State,
  type EtatFsrs,
  type Note,
  type Planificateur,
  type Resultat,
} from '../fsrs/planificateur';
import type { ReglagesPaquet } from '../paquets/reglages';
import type { Aleatoire } from './aleatoire';
import { melanger } from './aleatoire';
import {
  comparerOrdreAjout,
  composerListe,
  estEnApprentissage,
  type CarteDuPaquet,
  type DebutSeance,
} from './composition';

interface ContexteSeance {
  reglages: ReglagesPaquet;
  planificateur: Planificateur;
  aleatoire: Aleatoire;
}

export interface Seance {
  readonly contexte: ContexteSeance;
  /** Numéro de la phase en cours (1, 2, …), ou de la dernière phase si la séance est finie. */
  readonly phase: number;
  /** Cartes qui restent à voir dans la phase ; la première est la carte courante. */
  readonly file: readonly string[];
  /** Cartes déjà vues dans la phase : candidates à la phase suivante. */
  readonly vues: readonly string[];
  /** Cartes de la séance, avec leur état FSRS à jour. */
  readonly cartes: ReadonlyMap<string, CarteDuPaquet>;
  /** Cartes sorties de la séance sans être notées (suspendues ou supprimées). */
  readonly retirees: ReadonlySet<string>;
  /** Séance avant la dernière réponse, pour l'annuler. */
  readonly precedente: { readonly seance: Seance; readonly carteId: string } | null;
}

/** Révision à enregistrer dans le journal après une note. */
export interface RevisionANoter {
  carteId: string;
  note: Note;
  reviseLe: Date;
  avant: EtatFsrs;
  apres: EtatFsrs;
}

export interface ApresNote {
  seance: Seance;
  revision: RevisionANoter;
  resultat: Resultat;
  /**
   * Ordre par probabilité de rappel : la dernière carte de la phase 1 vient d'être vue.
   * L'interface affiche alors l'alerte, puis la séance continue avec les phases N.
   */
  alerteSeuil: boolean;
}

/** Début de séance : la liste des cartes est figée à cet instant. */
export function demarrerSeance(debut: DebutSeance): Seance {
  const liste = composerListe(debut);
  return {
    contexte: {
      reglages: debut.reglages,
      planificateur: debut.planificateur,
      aleatoire: debut.aleatoire,
    },
    phase: 1,
    file: liste.map((carte) => carte.id),
    vues: [],
    cartes: new Map(liste.map((carte) => [carte.id, carte])),
    retirees: new Set(),
    precedente: null,
  };
}

export function carteCourante(seance: Seance): CarteDuPaquet | null {
  const id = seance.file[0];
  return id === undefined ? null : seance.cartes.get(id)!;
}

export function estTerminee(seance: Seance): boolean {
  return seance.file.length === 0;
}

/** Cartes restant à voir dans la phase en cours, par état. */
export function compterFile(seance: Seance): {
  nouvelles: number;
  apprentissage: number;
  acquises: number;
} {
  const compte = { nouvelles: 0, apprentissage: 0, acquises: 0 };
  for (const id of seance.file) {
    const etat = seance.cartes.get(id)!.fsrs.state;
    if (etat === State.New) compte.nouvelles += 1;
    else if (etat === State.Review) compte.acquises += 1;
    else compte.apprentissage += 1;
  }
  return compte;
}

function ordonnerPhase(seance: Seance, cartes: CarteDuPaquet[], maintenant: Date): string[] {
  const { reglages, planificateur, aleatoire } = seance.contexte;
  switch (reglages.ordreCartes) {
    case 'hasard':
      return melanger(cartes, aleatoire).map((carte) => carte.id);
    case 'probabilite_rappel':
      return cartes
        .map((carte) => ({
          carte,
          rappel: planificateur.probabiliteRappel(carte.fsrs, maintenant),
        }))
        .sort((a, b) => a.rappel - b.rappel || comparerOrdreAjout(a.carte, b.carte))
        .map(({ carte }) => carte.id);
    case 'chronologique':
    case 'toutes_chronologique':
      return cartes.sort(comparerOrdreAjout).map((carte) => carte.id);
  }
}

/**
 * Fin d'une phase : la phase suivante reprend les cartes de la phase qui sont en
 * apprentissage et reviennent dans la fenêtre de débordement. Sinon la séance est finie ;
 * les autres cartes seront vues à la séance suivante.
 */
function finirPhase(seance: Seance, maintenant: Date): Seance {
  const limite = maintenant.getTime() + seance.contexte.reglages.fenetreDebordementMinutes * 60_000;
  const candidates = seance.vues
    .filter((id) => !seance.retirees.has(id))
    .map((id) => seance.cartes.get(id)!)
    .filter((carte) => estEnApprentissage(carte.fsrs) && carte.fsrs.due.getTime() <= limite);
  if (candidates.length === 0) return { ...seance, file: [], vues: [] };
  return {
    ...seance,
    phase: seance.phase + 1,
    file: ordonnerPhase(seance, candidates, maintenant),
    vues: [],
  };
}

/** Passe à la phase suivante si la file vient de se vider. */
function avancer(
  avant: Seance,
  apres: Seance,
  maintenant: Date,
): { seance: Seance; alerteSeuil: boolean } {
  if (avant.file.length === 0 || apres.file.length > 0)
    return { seance: apres, alerteSeuil: false };
  return {
    seance: finirPhase(apres, maintenant),
    alerteSeuil: avant.contexte.reglages.ordreCartes === 'probabilite_rappel' && avant.phase === 1,
  };
}

/** Note la carte courante (touche ou bouton de notation, au verso). */
export function noter(seance: Seance, note: Note, maintenant: Date): ApresNote {
  const carte = carteCourante(seance);
  if (carte === null) throw new Error('La séance est terminée : aucune carte à noter.');
  const resultat = seance.contexte.planificateur.noter(carte.id, carte.fsrs, maintenant, note);
  const cartes = new Map(seance.cartes).set(carte.id, { ...carte, fsrs: resultat.etat });
  const apres: Seance = {
    ...seance,
    file: seance.file.slice(1),
    vues: [...seance.vues, carte.id],
    cartes,
    precedente: { seance, carteId: carte.id },
  };
  const { seance: suivante, alerteSeuil } = avancer(seance, apres, maintenant);
  return {
    seance: suivante,
    revision: {
      carteId: carte.id,
      note,
      reviseLe: maintenant,
      avant: carte.fsrs,
      apres: resultat.etat,
    },
    resultat,
    alerteSeuil,
  };
}

function sansRetirees(seance: Seance, retirees: ReadonlySet<string>): Seance {
  return {
    ...seance,
    retirees,
    file: seance.file.filter((id) => !retirees.has(id)),
    vues: seance.vues.filter((id) => !retirees.has(id)),
  };
}

/**
 * Annule la dernière réponse : la carte est représentée avec son état FSRS d'avant la
 * réponse. Renvoie `null` s'il n'y a rien à annuler. L'appelant annule aussi la révision
 * dans le journal. Les cartes retirées depuis restent hors de la séance.
 */
export function annulerDerniereReponse(
  seance: Seance,
  maintenant: Date,
): { seance: Seance; carteId: string; alerteSeuil: boolean } | null {
  if (seance.precedente === null) return null;
  const { seance: precedente, carteId } = seance.precedente;
  const restauree = sansRetirees(precedente, seance.retirees);
  return { ...avancer(precedente, restauree, maintenant), carteId };
}

/**
 * Retire des cartes de la séance sans les noter : carte suspendue, ou supprimée par une
 * modification (trou supprimé, inverse décochée…). Si la carte courante est retirée, on
 * passe à la suivante.
 */
export function retirerCartes(
  seance: Seance,
  carteIds: readonly string[],
  maintenant: Date,
): { seance: Seance; alerteSeuil: boolean } {
  const retirees = new Set([...seance.retirees, ...carteIds]);
  return avancer(seance, sansRetirees(seance, retirees), maintenant);
}
